import {
	ChangeDetectionStrategy,
	Component,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	OnInit,
	signal,
	SimpleChanges,
	WritableSignal,
	inject,
} from '@angular/core'
import { Subject, takeUntil } from 'rxjs'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { PositionSettingsInterface } from '@shared/components/trading/widgets/positions-widget/core/interfaces/position-settings.interface'
import { ColumnInterface } from '@shared/interfaces/column.interface'
import { PositionsOptionsColumnsEnum } from '@shared/components/trading/widgets/positions-widget/core/enums/positions-options-columns.enum'
import { TradingLauncherService } from '@shared/services/trading-launcher.service'
import { PositionOptionInterface } from '@modules/options-module/positions/interfaces/position-option.interface'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { PricePipe } from '@shared/pipes/price.pipe'
import { ProfitPipe } from '@shared/pipes/profit.pipe'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { DatePipe, UpperCasePipe } from '@angular/common'
import { OptionTypeEnum } from '@modules/options-module/general/enums/option-type.enum'
import { PositionsOptionsActionsComponent } from '@shared/components/trading/positions/options/common/positions-options-actions/positions-options-actions.component'
import { PercentPipe } from '@shared/pipes/percent.pipe'
import { TableColumnComponent } from '@shared/components/common/table/core/components/table-column/table-column.component'
import { TableActionsComponent } from '@shared/components/common/table/core/components/table-actions/table-actions.component'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'
import { SecretDirective } from '@shared/directives/secret.directive'
import { TableFooterComponent } from '@shared/components/common/table/core/components/table-footer/table-footer.component'
import { TranslatePipe } from '@ngx-translate/core'
import { TuiSkeleton } from '@taiga-ui/kit'
import { ColumnSortInterface } from '@shared/components/common/table/core/interfaces/column-sort.interface'
import { WidgetsService } from '@modules/widgets-module/services/widgets.service'
import { TableComponent } from '@shared/components/common/table/table.component'
import { AssetTypesComponent } from '@shared/components/common/asset-type/asset-type.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-positions-options',
	templateUrl: './positions-options.component.html',
	styleUrl: './positions-options.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		HighlightDirective,
		PricePipe,
		ProfitPipe,
		QuantityPipe,
		DatePipe,
		UpperCasePipe,
		PositionsOptionsActionsComponent,
		PercentPipe,
		TableColumnComponent,
		TableActionsComponent,
		SecretDirective,
		TableFooterComponent,
		TranslatePipe,
		TuiSkeleton,
		TableComponent,
		AssetTypesComponent,
	],
})
export class PositionsOptionsComponent implements OnChanges, OnInit, OnDestroy {
	protected readonly positionsOptionsColumnsEnum: typeof PositionsOptionsColumnsEnum = PositionsOptionsColumnsEnum
	protected readonly number: NumberConstructor = Number
	protected readonly optionTypeEnum: typeof OptionTypeEnum = OptionTypeEnum

	public widget: InputSignal<WidgetInterface<PositionSettingsInterface> | null> =
		input<WidgetInterface<PositionSettingsInterface> | null>(null)

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _tradingLauncherService: TradingLauncherService =
		inject<TradingLauncherService>(TradingLauncherService)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)
	private readonly _widgetsService: WidgetsService = inject<WidgetsService>(WidgetsService)

	protected isLoadingPositions: WritableSignal<boolean> = signal<boolean>(false)
	protected positions: WritableSignal<PositionOptionInterface[]> = signal<PositionOptionInterface[]>([])
	protected statesColumns: WritableSignal<ColumnInterface<PositionsOptionsColumnsEnum>[]> = signal<
		ColumnInterface<PositionsOptionsColumnsEnum>[]
	>([])
	protected isSecret: WritableSignal<boolean> = signal<boolean>(false)
	private _listenSecret(): void {
		this._userHelperService.isSecret$.pipe(takeUntil(this._destroy$)).subscribe((value: boolean): void => {
			this.isSecret.set(value)
		})
	}

	private _updateStatesColumns(): void {
		const widget: WidgetInterface<PositionSettingsInterface> | null = this.widget()
		if (!widget) return

		this.statesColumns.set([...widget.settings.options.columns])
	}

	private _launchPositionsOptions(): void {
		this._tradingLauncherService
			.launchPositionsOptions$()
			.pipe(takeUntil(this._destroy$))
			.subscribe(([positions, loading]: [PositionOptionInterface[], boolean]): void => {
				this.positions.set(positions)
				this.isLoadingPositions.set(loading)
			})
	}

	protected changeSort(activeSort: ColumnSortInterface<keyof PositionOptionInterface>): void {
		const widget: WidgetInterface<PositionSettingsInterface> | null = this.widget()
		if (!widget) return

		this._widgetsService.updateWidget({
			...widget,
			settings: {
				...widget.settings,
				options: {
					...widget.settings.options,
					activeSort: activeSort,
				},
			},
		})
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['widget']) {
			this._updateStatesColumns()
		}
	}

	ngOnInit(): void {
		this._listenSecret()
		this._launchPositionsOptions()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
